import {
  PHARMACY_HALO_COLORS,
  DOCLINK_COLORS,
  QUDA_COLORS,
  MEDIDROP_COLORS,
  CHERUB_COLORS,
} from "@/config/colors";

const HUBMED_HEADER = "#5b21b6";
const HUBMED_HEADER_INNER = "#5b21b6";

/** Admin overview top row: date strip + three stat cards share this height */
export const ADMIN_TOP_CARD_HEIGHT = 140;

/** Widen Syncfusion date-range input inside colored admin tiles (dark bar + white text). */
export const adminDateRangePickerWideNestedSx = {
  "& .e-daterangepicker.e-control-wrapper": {
    width: "100% !important",
    maxWidth: "100%",
    minWidth: "min(100%, 360px)",
  },
  "& .e-input-group": {
    width: "100% !important",
    maxWidth: "100%",
    minWidth: "min(100%, 360px)",
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "stretch",
  },
  "& .e-input-group input.e-input": {
    color: "white !important",
    flex: "1 1 auto",
    minWidth: "10em",
    width: "auto !important",
  },
  "& .e-input-group.e-control-wrapper input.e-input": {
    color: "white !important",
    flex: "1 1 auto",
    minWidth: "10em",
    width: "auto !important",
  },
  "& .e-input-group-icon": {
    color: "white !important",
    flex: "0 0 auto",
    minWidth: 28,
    width: 28,
    padding: "0 4px",
  },
  "& .e-clear-icon": { color: "white !important" },
  "& label": { color: "rgba(255,255,255,0.9) !important" },
};

/**
 * Syncfusion date range inside blue bar — white inputs, wider inner field, fixed row height.
 */
export const adminDateRangeBlueBoxSx = {
  borderRadius: "8px",
  py: 1.5,
  px: 1.5,
  height: ADMIN_TOP_CARD_HEIGHT,
  minHeight: ADMIN_TOP_CARD_HEIGHT,
  maxHeight: ADMIN_TOP_CARD_HEIGHT,
  boxSizing: "border-box",
  width: "100%",
  minWidth: 0,
  backgroundColor: PHARMACY_HALO_COLORS.BLUE,
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  "& .text-black": { color: "white !important" },
  ...adminDateRangePickerWideNestedSx,
};

/** MUI DataGrid v7: header cells need explicit bg on `.MuiDataGrid-columnHeader` or headers stay paper-white. */
const haloHeaderNested = {
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: `${PHARMACY_HALO_COLORS.BLUE} !important`,
    color: "#fff !important",
    borderBottom: `2px solid ${PHARMACY_HALO_COLORS.BLUE}`,
    "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.35)" },
    "& .MuiDataGrid-sortIcon": { color: "#fff !important", opacity: 1 },
    "& .MuiDataGrid-menuIconButton": { color: "#fff !important" },
  },
  "& .MuiDataGrid-columnHeadersInner": {
    backgroundColor: `${PHARMACY_HALO_COLORS.BLUE} !important`,
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: `${PHARMACY_HALO_COLORS.BLUE} !important`,
    color: "#fff !important",
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader--sorted": {
    backgroundColor: `${PHARMACY_HALO_COLORS.BLUE} !important`,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#fff !important",
    fontWeight: 700,
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    color: "#fff !important",
  },
};

/** No focus ring when clicking cells (DataGrid default outline / inset shadow). Reusable on any MUI DataGrid. */
export const dataGridCellNoFocus = {
  "& .MuiDataGrid-cell:focus": {
    outline: "none !important",
    boxShadow: "none !important",
  },
  "& .MuiDataGrid-cell:focus-within": {
    outline: "none !important",
    boxShadow: "none !important",
  },
  "& .MuiDataGrid-cell--focused": {
    outline: "none !important",
    boxShadow: "none !important",
  },
};

/** Blue headers, uniform white rows, yellow hover (no zebra — avoids clashing with bool cells). */
export const pharmacyHaloDataGridSx = {
  border: "none",
  "& .MuiDataGrid-row": {
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: `${PHARMACY_HALO_COLORS.YELLOW} !important`,
      opacity: 0.9,
    },
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #e5e7eb",
    fontSize: 14,
    outline: "none !important",
    "&:focus": {
      outline: "none !important",
      boxShadow: "none !important",
    },
    "&:focus-within": {
      outline: "none !important",
      boxShadow: "none !important",
    },
  },
  "& .MuiDataGrid-row.admin-row-selected": {
    backgroundColor: "rgba(0, 33, 105, 0.1) !important",
    "&:hover": {
      backgroundColor: "rgba(0, 33, 105, 0.14) !important",
    },
  },
  ...haloHeaderNested,
  ...dataGridCellNoFocus,
};

const docLinkHeaderNested = {
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: `${DOCLINK_COLORS.DARK_PURPLE} !important`,
    color: "#fff !important",
    borderBottom: `2px solid ${DOCLINK_COLORS.DARK_PURPLE}`,
    "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.35)" },
    "& .MuiDataGrid-sortIcon": { color: "#fff !important", opacity: 1 },
    "& .MuiDataGrid-menuIconButton": { color: "#fff !important" },
  },
  "& .MuiDataGrid-columnHeadersInner": {
    backgroundColor: `${DOCLINK_COLORS.DARK_PURPLE} !important`,
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: `${DOCLINK_COLORS.DARK_PURPLE} !important`,
    color: "#fff !important",
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader--sorted": {
    backgroundColor: `${DOCLINK_COLORS.DARK_PURPLE} !important`,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#fff !important",
    fontWeight: 700,
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    color: "#fff !important",
  },
};

export const docLinkPurpleDataGridSx = {
  border: "none",
  "& .MuiDataGrid-row": {
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: `${DOCLINK_COLORS.LAVENDER_TINT} !important`,
    },
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #e5e7eb",
    fontSize: 14,
    outline: "none !important",
    "&:focus": {
      outline: "none !important",
      boxShadow: "none !important",
    },
    "&:focus-within": {
      outline: "none !important",
      boxShadow: "none !important",
    },
  },
  ...docLinkHeaderNested,
  ...dataGridCellNoFocus,
};

/** Admin Quda overview table — teal headers, white rows, teal-tint hover (no zebra). */
export const qudaAdminDataGridSx = {
  border: "none",
  "& .MuiDataGrid-row": {
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: `${QUDA_COLORS.TEAL_LIGHT} !important`,
    },
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #e5e7eb",
    fontSize: 14,
    outline: "none !important",
    "&:focus": {
      outline: "none !important",
      boxShadow: "none !important",
    },
    "&:focus-within": {
      outline: "none !important",
      boxShadow: "none !important",
    },
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: `${QUDA_COLORS.TEAL_DARK} !important`,
    color: "#fff !important",
    borderBottom: `2px solid ${QUDA_COLORS.TEAL_DARK}`,
    "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.35)" },
    "& .MuiDataGrid-sortIcon": { color: "#fff !important", opacity: 1 },
    "& .MuiDataGrid-menuIconButton": { color: "#fff !important" },
  },
  "& .MuiDataGrid-columnHeadersInner": {
    backgroundColor: `${QUDA_COLORS.TEAL_DARK} !important`,
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: `${QUDA_COLORS.TEAL_DARK} !important`,
    color: "#fff !important",
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader--sorted": {
    backgroundColor: `${QUDA_COLORS.TEAL_DARK} !important`,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 700,
    color: "#fff !important",
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    color: "#fff !important",
  },
  ...dataGridCellNoFocus,
};

const hubMedHeaderNested = {
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: `${HUBMED_HEADER} !important`,
    color: "#fff !important",
    borderBottom: `2px solid ${HUBMED_HEADER}`,
    "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.35)" },
    "& .MuiDataGrid-sortIcon": { color: "#fff !important", opacity: 1 },
    "& .MuiDataGrid-menuIconButton": { color: "#fff !important" },
  },
  "& .MuiDataGrid-columnHeadersInner": {
    backgroundColor: `${HUBMED_HEADER_INNER} !important`,
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: `${HUBMED_HEADER} !important`,
    color: "#fff !important",
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader--sorted": {
    backgroundColor: `${HUBMED_HEADER} !important`,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#fff !important",
    fontWeight: 700,
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    color: "#fff !important",
  },
};

/** MediDrop admin table — orange headers, soft orange-tint row hover */
export const mediDropDataGridSx = {
  border: "none",
  "& .MuiDataGrid-row": {
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: `${MEDIDROP_COLORS.ORANGE_TINT} !important`,
    },
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #e5e7eb",
    fontSize: 14,
    outline: "none !important",
    "&:focus": {
      outline: "none !important",
      boxShadow: "none !important",
    },
    "&:focus-within": {
      outline: "none !important",
      boxShadow: "none !important",
    },
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: `${MEDIDROP_COLORS.ORANGE} !important`,
    color: "#fff !important",
    borderBottom: `2px solid ${MEDIDROP_COLORS.ORANGE} !important`,
    "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.35)" },
    "& .MuiDataGrid-sortIcon": { color: "#fff !important", opacity: 1 },
    "& .MuiDataGrid-menuIconButton": { color: "#fff !important" },
  },
  "& .MuiDataGrid-columnHeadersInner": {
    backgroundColor: `${MEDIDROP_COLORS.ORANGE} !important`,
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: `${MEDIDROP_COLORS.ORANGE} !important`,
    color: "#fff !important",
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader--sorted": {
    backgroundColor: `${MEDIDROP_COLORS.ORANGE} !important`,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#fff !important",
    fontWeight: 700,
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    color: "#fff !important",
  },
  ...dataGridCellNoFocus,
};

/** Cherub admin tables — sky-blue headers, soft sky-tint row hover */
export const cherubDataGridSx = {
  border: "none",
  "& .MuiDataGrid-row": {
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: `${CHERUB_COLORS.SKY_TINT} !important`,
    },
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #e5e7eb",
    fontSize: 14,
    outline: "none !important",
    "&:focus": { outline: "none !important", boxShadow: "none !important" },
    "&:focus-within": { outline: "none !important", boxShadow: "none !important" },
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: `${CHERUB_COLORS.SKY_DARK} !important`,
    color: "#fff !important",
    borderBottom: `2px solid ${CHERUB_COLORS.SKY_DARK}`,
    "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,0.35)" },
    "& .MuiDataGrid-sortIcon": { color: "#fff !important", opacity: 1 },
    "& .MuiDataGrid-menuIconButton": { color: "#fff !important" },
  },
  "& .MuiDataGrid-columnHeadersInner": {
    backgroundColor: `${CHERUB_COLORS.SKY_DARK} !important`,
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: `${CHERUB_COLORS.SKY_DARK} !important`,
    color: "#fff !important",
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader--sorted": {
    backgroundColor: `${CHERUB_COLORS.SKY_DARK} !important`,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#fff !important",
    fontWeight: 700,
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    color: "#fff !important",
  },
  ...dataGridCellNoFocus,
};

/** HubMed admin tables — violet headers, soft violet row hover */
export const hubMedAdminDataGridSx = {
  border: "none",
  "& .MuiDataGrid-row": {
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: "#ede9fe !important",
    },
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #e5e7eb",
    fontSize: 14,
    outline: "none !important",
    "&:focus": {
      outline: "none !important",
      boxShadow: "none !important",
    },
    "&:focus-within": {
      outline: "none !important",
      boxShadow: "none !important",
    },
  },
  ...hubMedHeaderNested,
  ...dataGridCellNoFocus,
};
